import React from "react";
import { Wand2 } from "lucide-react";
import { updateAppThumbnails } from "../utils/thumbnailMapper";
import { SalesCopy } from './appSalesCopy';

// App data structure
interface App {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: React.ReactNode;
  image: string;
  url?: string;
  popular?: boolean;
  new?: boolean;
  comingSoon?: boolean;
  price?: number;
  longDescription?: string;
  demoImage?: string;
  benefits?: string[];
  features?: {
    title: string;
    description: string;
    icon?: React.ReactNode;
  }[];
  steps?: {
    title: string;
    description: string;
  }[];
  useCases?: {
    title: string;
    description: string;
    points: string[];
  }[];
  testimonials?: {
    quote: string;
    name: string;
    role: string;
    avatar: string;
  }[];
  faqs?: {
    question: string;
    answer: string;
  }[];
  tags?: string[];
  salesCopy?: SalesCopy;
  group: string;
  skillSlug?: string;
  productName?: string;
  targetAudience?: string;
}

const rawAppsData: App[] = [
  {
    id: "project-graveyard",
    name: "Project Graveyard",
    description: "Finds every side project you abandoned, tells you why each one died, and helps you finish the one worth going back to.",
    category: "agent-skills",
    group: "agent-skills",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/project-graveyard-thumbnail.png",
    url: "/ai-runner/project-graveyard",
    popular: false,
  },
  {
    id: "first-reader",
    name: "First Reader",
    description: "Simulates real readers going through your draft and reports where they lose interest, where they stop reading, and what they remember afterward, without rewriting a word.",
    category: "agent-skills",
    group: "agent-skills",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/first-reader-thumbnail.png",
    url: "/ai-runner/first-reader",
    popular: false,
  },
  {
    id: "scope-creep-detector",
    name: "Scope Creep Detector",
    description: "Checks whether a diff grew beyond its stated intent and recommends what to keep, split, or justify.",
    category: "agent-skills",
    group: "agent-skills",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/scope-creep-detector-thumbnail.png",
    url: "/ai-runner/scope-creep-detector",
    popular: false,
  },
  {
    id: "commit-archaeologist",
    name: "Commit Archaeologist",
    description: "Reconstructs why a file or code region exists from its introducing commit, later edits, co-changes, and intent clues.",
    category: "agent-skills",
    group: "agent-skills",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/commit-archaeologist-thumbnail.png",
    url: "/ai-runner/commit-archaeologist",
    popular: false,
  },
  {
    id: "dependency-doctor",
    name: "Dependency Doctor",
    description: "Checks a dependency manifest for standard-library pins, obsolete backports, unpinned entries, duplicate constraints, and yanked releases.",
    category: "agent-skills",
    group: "agent-skills",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/dependency-doctor-thumbnail.png",
    url: "/ai-runner/dependency-doctor",
    popular: false,
  },
  {
    id: "advisor-orchestrator-worker",
    name: "Advisor Orchestrator Worker",
    description: "Meta Loop with Claude Fable 5.1 as advisor, GPT-6 Astra as orchestrator, and Gemini 3.8 Flash as worker.",
    category: "agent-skills",
    group: "agent-skills",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/advisor-orchestrator-worker-thumbnail.png",
    url: "/ai-runner/advisor-orchestrator-worker",
    popular: false,
  },
  {
    id: "thinking-out-loud",
    name: "Thinking Out Loud",
    description: "Echoes a voice ramble back as a scannable brief, with the model's guesses quarantined and your reversals flagged.",
    category: "agent-skills",
    group: "agent-skills",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/thinking-out-loud-thumbnail.png",
    url: "/ai-runner/thinking-out-loud",
    popular: false,
  },
  {
    id: "self-improving-agent-skills",
    name: "Self-Improving Agent Skills",
    description: "Automatically optimize agent skills using Gemini and ADK.",
    category: "agent-skills",
    group: "agent-skills",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/self-improving-agent-skills-thumbnail.png",
    url: "/ai-runner/self-improving-agent-skills",
    popular: false,
  },
  {
    id: "ai-blog-to-podcast-agent",
    name: "AI Blog to Podcast Agent",
    description: "Turn any blog URL into a narrated podcast episode.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-blog-to-podcast-agent-thumbnail.png",
    url: "/ai-runner/ai-blog-to-podcast-agent",
    popular: false,
  },
  {
    id: "ai-breakup-recovery-agent",
    name: "AI Breakup Recovery Agent",
    description: "An agent team that talks you through the post-breakup spiral.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-breakup-recovery-agent-thumbnail.png",
    url: "/ai-runner/ai-breakup-recovery-agent",
    popular: false,
  },
  {
    id: "ai-data-analysis-agent",
    name: "AI Data Analysis Agent",
    description: "Ask questions of any CSV or Excel file in plain English.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-data-analysis-agent-thumbnail.png",
    url: "/ai-runner/ai-data-analysis-agent",
    popular: false,
  },
  {
    id: "ai-medical-imaging-agent",
    name: "AI Medical Imaging Agent",
    description: "Diagnostic analysis of X-rays and scans with Gemini.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-medical-imaging-agent-thumbnail.png",
    url: "/ai-runner/ai-medical-imaging-agent",
    popular: false,
  },
  {
    id: "ai-meme-generator-agent-browser",
    name: "AI Meme Generator Agent (Browser)",
    description: "Makes memes by driving a real browser, not an image API.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-meme-generator-agent-browser-thumbnail.png",
    url: "/ai-runner/ai-meme-generator-agent-browser",
    popular: false,
  },
  {
    id: "ai-music-generator-agent",
    name: "AI Music Generator Agent",
    description: "Prompt in, MP3 track out.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-music-generator-agent-thumbnail.png",
    url: "/ai-runner/ai-music-generator-agent",
    popular: false,
  },
  {
    id: "ai-travel-agent-local-cloud",
    name: "AI Travel Agent (Local & Cloud)",
    description: "Personalized day-by-day travel itineraries.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-travel-agent-local-cloud-thumbnail.png",
    url: "/ai-runner/ai-travel-agent-local-cloud",
    popular: false,
  },
  {
    id: "ai-x402-paying-agent",
    name: "AI x402 Paying Agent",
    description: "An agent with a wallet that pays per-call for the data it needs — no API keys.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-x402-paying-agent-thumbnail.png",
    url: "/ai-runner/ai-x402-paying-agent",
    popular: false,
  },
  {
    id: "gemini-multimodal-agent",
    name: "Gemini Multimodal Agent",
    description: "Video analysis plus web search in one agent.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/gemini-multimodal-agent-thumbnail.png",
    url: "/ai-runner/gemini-multimodal-agent",
    popular: false,
  },
  {
    id: "mixture-of-agents",
    name: "Mixture of Agents",
    description: "Multiple LLMs answer, one aggregates the best response.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/mixture-of-agents-thumbnail.png",
    url: "/ai-runner/mixture-of-agents",
    popular: false,
  },
  {
    id: "xai-finance-agent",
    name: "xAI Finance Agent",
    description: "Real-time stock analysis powered by Grok.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/xai-finance-agent-thumbnail.png",
    url: "/ai-runner/xai-finance-agent",
    popular: false,
  },
  {
    id: "openai-research-agent",
    name: "OpenAI Research Agent",
    description: "Multi-agent topic research with the OpenAI Agents SDK.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/openai-research-agent-thumbnail.png",
    url: "/ai-runner/openai-research-agent",
    popular: false,
  },
  {
    id: "web-scraping-ai-agent",
    name: "Web Scraping AI Agent",
    description: "Describe what to extract and the agent scrapes it.",
    category: "starter-ai-agents",
    group: "starter-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/web-scraping-ai-agent-thumbnail.png",
    url: "/ai-runner/web-scraping-ai-agent",
    popular: false,
  },
  {
    id: "ai-home-renovation-agent",
    name: "AI Home Renovation Agent with Nano Banana Pro",
    description: "Photos of your space in, renovation plan and photorealistic renders out.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-home-renovation-agent-thumbnail.png",
    url: "/ai-runner/ai-home-renovation-agent",
    popular: false,
  },
  {
    id: "devpulse-ai",
    name: "DevPulse AI - Multi-Agent Signal Intelligence",
    description: "Aggregates and scores technical signals into a daily intelligence digest.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/devpulse-ai-thumbnail.png",
    url: "/ai-runner/devpulse-ai",
    popular: false,
  },
  {
    id: "ai-deep-research-agent",
    name: "AI Deep Research Agent",
    description: "Comprehensive web research with the OpenAI Agents SDK and Firecrawl.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-deep-research-agent-thumbnail.png",
    url: "/ai-runner/ai-deep-research-agent",
    popular: false,
  },
  {
    id: "ai-vc-due-diligence-agent-team",
    name: "AI VC Due Diligence Agent Team",
    description: "Multi-agent startup investment analysis with Gemini 3.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-vc-due-diligence-agent-team-thumbnail.png",
    url: "/ai-runner/ai-vc-due-diligence-agent-team",
    popular: false,
  },
  {
    id: "research-agent-gemini-interaction-api",
    name: "AI Research Planner & Executor (Google Interactions API)",
    description: "Multi-phase research with stateful conversations and auto-generated infographics.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/research-agent-gemini-interaction-api-thumbnail.png",
    url: "/ai-runner/research-agent-gemini-interaction-api",
    popular: false,
  },
  {
    id: "ai-consultant-agent",
    name: "AI Consultant Agent",
    description: "Market analysis and strategy recommendations with live web research.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-consultant-agent-thumbnail.png",
    url: "/ai-runner/ai-consultant-agent",
    popular: false,
  },
  {
    id: "ai-system-architect-agent",
    name: "AI System Architect Agent",
    description: "Architecture reviews using DeepSeek R1 reasoning plus Claude.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-system-architect-agent-thumbnail.png",
    url: "/ai-runner/ai-system-architect-agent",
    popular: false,
  },
  {
    id: "ai-financial-coach-agent",
    name: "AI Financial Coach Agent",
    description: "Personalized budget, debt, and savings analysis.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-financial-coach-agent-thumbnail.png",
    url: "/ai-runner/ai-financial-coach-agent",
    popular: false,
  },
  {
    id: "ai-movie-production-agent",
    name: "AI Movie Production Agent",
    description: "Script drafts and casting ideas from a one-line movie concept.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-movie-production-agent-thumbnail.png",
    url: "/ai-runner/ai-movie-production-agent",
    popular: false,
  },
  {
    id: "ai-investment-agent",
    name: "AI Investment Agent",
    description: "Stock comparison reports built on Yahoo Finance data.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-investment-agent-thumbnail.png",
    url: "/ai-runner/ai-investment-agent",
    popular: false,
  },
  {
    id: "earnings-call-analyst-agent",
    name: "Earnings Call Analyst Agent",
    description: "Turns YouTube earnings calls into a playback-synced analyst workspace.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/earnings-call-analyst-agent-thumbnail.png",
    url: "/ai-runner/earnings-call-analyst-agent",
    popular: false,
  },
  {
    id: "ai-health-fitness-agent",
    name: "AI Health & Fitness Agent",
    description: "Tailored diet and workout plans from your goals.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-health-fitness-agent-thumbnail.png",
    url: "/ai-runner/ai-health-fitness-agent",
    popular: false,
  },
  {
    id: "ai-product-launch-intelligence-agent",
    name: "AI Product Launch Intelligence Agent",
    description: "Go-to-market intelligence on competitor launches.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-product-launch-intelligence-agent-thumbnail.png",
    url: "/ai-runner/ai-product-launch-intelligence-agent",
    popular: false,
  },
  {
    id: "ai-fraud-investigation-agent",
    name: "AI Fraud Investigation Agent",
    description: "Cross-references public records to flag facilities that don't add up.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-fraud-investigation-agent-thumbnail.png",
    url: "/ai-runner/ai-fraud-investigation-agent",
    popular: false,
  },
  {
    id: "ai-journalist-agent",
    name: "AI Journalist Agent",
    description: "Researches, writes, and edits articles on any topic.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-journalist-agent-thumbnail.png",
    url: "/ai-runner/ai-journalist-agent",
    popular: false,
  },
  {
    id: "ai-mental-wellbeing-agent",
    name: "AI Mental Wellbeing Agent",
    description: "A coordinated agent team for mental health support plans.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-mental-wellbeing-agent-thumbnail.png",
    url: "/ai-runner/ai-mental-wellbeing-agent",
    popular: false,
  },
  {
    id: "ai-meeting-agent",
    name: "AI Meeting Agent",
    description: "Context, industry insights, and strategy briefs before you walk in.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-meeting-agent-thumbnail.png",
    url: "/ai-runner/ai-meeting-agent",
    popular: false,
  },
  {
    id: "ai-self-evolving-agent",
    name: "AI Self-Evolving Agent",
    description: "Agents that rewrite their own workflows with EvoAgentX.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-self-evolving-agent-thumbnail.png",
    url: "/ai-runner/ai-self-evolving-agent",
    popular: false,
  },
  {
    id: "ai-sales-intelligence-agent-team",
    name: "AI Sales Intelligence Agent Team",
    description: "Generates competitive sales battle cards in real time.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-sales-intelligence-agent-team-thumbnail.png",
    url: "/ai-runner/ai-sales-intelligence-agent-team",
    popular: false,
  },
  {
    id: "ai-social-media-news-and-podcast-agent",
    name: "AI Social Media News and Podcast Agent",
    description: "Curates your trusted sources into briefs and generated podcasts.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-social-media-news-and-podcast-agent-thumbnail.png",
    url: "/ai-runner/ai-social-media-news-and-podcast-agent",
    popular: false,
  },
  {
    id: "trust-gated-multi-agent-research-team",
    name: "Trust-Gated Multi-Agent Research Team",
    description: "Every agent verified, every action in a hash-chained audit trail.",
    category: "advanced-ai-agents",
    group: "advanced-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/trust-gated-multi-agent-research-team-thumbnail.png",
    url: "/ai-runner/trust-gated-multi-agent-research-team",
    popular: false,
  },
  {
    id: "always-on-hacker-news-briefing-agent",
    name: "Always-on Hacker News Briefing Agent",
    description: "A scheduled scout that ships a ranked daily brief to Slack or email.",
    category: "always-on-agents",
    group: "always-on-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/always-on-hacker-news-briefing-agent-thumbnail.png",
    url: "/ai-runner/always-on-hacker-news-briefing-agent",
    popular: false,
  },
  {
    id: "release-radar-agent",
    name: "Release Radar Agent",
    description: "Watches dependency releases and briefs you on breaking, deprecated, security, and major-version changes.",
    category: "always-on-agents",
    group: "always-on-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/release-radar-agent-thumbnail.png",
    url: "/ai-runner/release-radar-agent",
    popular: false,
  },
  {
    id: "ai-competitor-intelligence-agent-team",
    name: "AI Competitor Intelligence Agent Team",
    description: "Structured competitor teardowns built from their own websites.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-competitor-intelligence-agent-team-thumbnail.png",
    url: "/ai-runner/ai-competitor-intelligence-agent-team",
    popular: false,
  },
  {
    id: "ai-finance-agent-team",
    name: "AI Finance Agent Team",
    description: "A financial analyst team in 20 lines of Python.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-finance-agent-team-thumbnail.png",
    url: "/ai-runner/ai-finance-agent-team",
    popular: false,
  },
  {
    id: "ai-game-design-agent-team",
    name: "AI Game Design Agent Team",
    description: "Full game concepts from a swarm of design specialists.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-game-design-agent-team-thumbnail.png",
    url: "/ai-runner/ai-game-design-agent-team",
    popular: false,
  },
  {
    id: "ag2-adaptive-research-team",
    name: "AG2 Adaptive Research Team",
    description: "Agent teamwork with routing and fallback, built on AG2.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ag2-adaptive-research-team-thumbnail.png",
    url: "/ai-runner/ag2-adaptive-research-team",
    popular: false,
  },
  {
    id: "ai-legal-agent-team-cloud-local",
    name: "AI Legal Agent Team (Cloud & Local)",
    description: "Research, contract analysis, and strategy from a full legal bench.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-legal-agent-team-cloud-local-thumbnail.png",
    url: "/ai-runner/ai-legal-agent-team-cloud-local",
    popular: false,
  },
  {
    id: "ai-recruitment-agent-team",
    name: "AI Recruitment Agent Team",
    description: "Resume screening to interview scheduling, end to end.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-recruitment-agent-team-thumbnail.png",
    url: "/ai-runner/ai-recruitment-agent-team",
    popular: false,
  },
  {
    id: "ai-real-estate-agent-team",
    name: "AI Real Estate Agent Team",
    description: "Property search, market analysis, and recommendations.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-real-estate-agent-team-thumbnail.png",
    url: "/ai-runner/ai-real-estate-agent-team",
    popular: false,
  },
  {
    id: "ai-services-agency-crewai",
    name: "AI Services Agency (CrewAI)",
    description: "A digital agency that scopes and plans your software project.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-services-agency-crewai-thumbnail.png",
    url: "/ai-runner/ai-services-agency-crewai",
    popular: false,
  },
  {
    id: "ai-teaching-agent-team",
    name: "AI Teaching Agent Team",
    description: "A faculty of agents that builds your complete learning path.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-teaching-agent-team-thumbnail.png",
    url: "/ai-runner/ai-teaching-agent-team",
    popular: false,
  },
  {
    id: "multimodal-coding-agent-team",
    name: "Multimodal Coding Agent Team",
    description: "Snap a photo of a coding problem, get a sandboxed solution.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/multimodal-coding-agent-team-thumbnail.png",
    url: "/ai-runner/multimodal-coding-agent-team",
    popular: false,
  },
  {
    id: "multimodal-design-agent-team",
    name: "Multimodal Design Agent Team",
    description: "Design critiques from a Gemini-powered expert panel.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/multimodal-design-agent-team-thumbnail.png",
    url: "/ai-runner/multimodal-design-agent-team",
    popular: false,
  },
  {
    id: "multimodal-ui-ux-feedback-agent-team",
    name: "Multimodal UI/UX Feedback Agent Team",
    description: "Landing page feedback plus an auto-generated improved version.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/multimodal-ui-ux-feedback-agent-team-thumbnail.png",
    url: "/ai-runner/multimodal-ui-ux-feedback-agent-team",
    popular: false,
  },
  {
    id: "ai-travel-planner-agent-team",
    name: "AI Travel Planner Agent Team",
    description: "A complete trip itinerary, crafted by a team.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-travel-planner-agent-team-thumbnail.png",
    url: "/ai-runner/ai-travel-planner-agent-team",
    popular: false,
  },
  {
    id: "llm-panel-agent-team",
    name: "LLM Panel Agent Team",
    description: "Three vendors review the same diff blind, then argue it out anonymously.",
    category: "multi-agent-teams",
    group: "multi-agent-teams",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/llm-panel-agent-team-thumbnail.png",
    url: "/ai-runner/llm-panel-agent-team",
    popular: false,
  },
  {
    id: "ai-audio-tour-agent",
    name: "AI Audio Tour Agent",
    description: "Self-guided audio tours from your location, interests, and pace.",
    category: "voice-ai-agents",
    group: "voice-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-audio-tour-agent-thumbnail.png",
    url: "/ai-runner/ai-audio-tour-agent",
    popular: false,
  },
  {
    id: "customer-support-voice-agent",
    name: "Customer Support Voice Agent",
    description: "Voice answers grounded in your own docs.",
    category: "voice-ai-agents",
    group: "voice-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/customer-support-voice-agent-thumbnail.png",
    url: "/ai-runner/customer-support-voice-agent",
    popular: false,
  },
  {
    id: "insurance-claim-live-agent-team",
    name: "Insurance Claim Live Agent Team",
    description: "Voice claim intake on Gemini 3.8 Live that writes a field notebook, looks at damage through the webcam, and sketches the incident.",
    category: "voice-ai-agents",
    group: "voice-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/insurance-claim-live-agent-team-thumbnail.png",
    url: "/ai-runner/insurance-claim-live-agent-team",
    popular: false,
  },
  {
    id: "voice-rag-agent-openai-sdk",
    name: "Voice RAG Agent (OpenAI SDK)",
    description: "Ask your PDFs questions, hear the answers.",
    category: "voice-ai-agents",
    group: "voice-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/voice-rag-agent-openai-sdk-thumbnail.png",
    url: "/ai-runner/voice-rag-agent-openai-sdk",
    popular: false,
  },
  {
    id: "generative-ui-starter-project",
    name: "Generative UI Starter Project",
    description: "A chat-driven kanban board you and the agent work together.",
    category: "generative-ui-agents",
    group: "generative-ui-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/generative-ui-starter-project-thumbnail.png",
    url: "/ai-runner/generative-ui-starter-project",
    popular: false,
  },
  {
    id: "ai-dashboard-canvas-agent",
    name: "AI Dashboard Canvas Agent",
    description: "Describe a dashboard in chat, charts assemble on a live canvas.",
    category: "generative-ui-agents",
    group: "generative-ui-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-dashboard-canvas-agent-thumbnail.png",
    url: "/ai-runner/ai-dashboard-canvas-agent",
    popular: false,
  },
  {
    id: "ai-mcp-app-builder",
    name: "AI MCP App Builder",
    description: "Describe an MCP app, get a live sandboxed instance back.",
    category: "generative-ui-agents",
    group: "generative-ui-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-mcp-app-builder-thumbnail.png",
    url: "/ai-runner/ai-mcp-app-builder",
    popular: false,
  },
  {
    id: "mcp-apps-generative-ui-showcase",
    name: "MCP Apps Generative UI Showcase",
    description: "MCP apps that render real interactive UI, flight search included.",
    category: "generative-ui-agents",
    group: "generative-ui-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/mcp-apps-generative-ui-showcase-thumbnail.png",
    url: "/ai-runner/mcp-apps-generative-ui-showcase",
    popular: false,
  },
  {
    id: "ai-shadcn-component-generator",
    name: "AI Shadcn Component Generator",
    description: "Chat your way to production-ready shadcn components.",
    category: "generative-ui-agents",
    group: "generative-ui-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-shadcn-component-generator-thumbnail.png",
    url: "/ai-runner/ai-shadcn-component-generator",
    popular: false,
  },
  {
    id: "ai-3d-pygame-agent",
    name: "AI 3D Pygame Agent",
    description: "DeepSeek R1 writes PyGame code, browser agents run it live.",
    category: "autonomous-game-playing-agents",
    group: "autonomous-game-playing-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-3d-pygame-agent-thumbnail.png",
    url: "/ai-runner/ai-3d-pygame-agent",
    popular: false,
  },
  {
    id: "ai-chess-agent",
    name: "AI Chess Agent",
    description: "Agent White vs Agent Black with validated moves.",
    category: "autonomous-game-playing-agents",
    group: "autonomous-game-playing-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-chess-agent-thumbnail.png",
    url: "/ai-runner/ai-chess-agent",
    popular: false,
  },
  {
    id: "ai-tic-tac-toe-agent",
    name: "AI Tic-Tac-Toe Agent",
    description: "Two different LLMs battle it out, move by move.",
    category: "autonomous-game-playing-agents",
    group: "autonomous-game-playing-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-tic-tac-toe-agent-thumbnail.png",
    url: "/ai-runner/ai-tic-tac-toe-agent",
    popular: false,
  },
  {
    id: "browser-mcp-agent",
    name: "Browser MCP Agent",
    description: "Drive a real browser with natural language over MCP.",
    category: "mcp-ai-agents",
    group: "mcp-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/browser-mcp-agent-thumbnail.png",
    url: "/ai-runner/browser-mcp-agent",
    popular: false,
  },
  {
    id: "github-mcp-agent",
    name: "GitHub MCP Agent",
    description: "Explore and analyze any repo in plain English.",
    category: "mcp-ai-agents",
    group: "mcp-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/github-mcp-agent-thumbnail.png",
    url: "/ai-runner/github-mcp-agent",
    popular: false,
  },
  {
    id: "notion-mcp-agent",
    name: "Notion MCP Agent",
    description: "Talk to your Notion pages from the terminal.",
    category: "mcp-ai-agents",
    group: "mcp-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/notion-mcp-agent-thumbnail.png",
    url: "/ai-runner/notion-mcp-agent",
    popular: false,
  },
  {
    id: "ai-travel-planner-mcp-agent",
    name: "AI Travel Planner MCP Agent",
    description: "Itineraries built on live Airbnb and Google Maps data.",
    category: "mcp-ai-agents",
    group: "mcp-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-travel-planner-mcp-agent-thumbnail.png",
    url: "/ai-runner/ai-travel-planner-mcp-agent",
    popular: false,
  },
  {
    id: "multi-mcp-agent-router",
    name: "Multi-MCP Agent Router",
    description: "Specialist agents, each wired to its own MCP server.",
    category: "mcp-ai-agents",
    group: "mcp-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/multi-mcp-agent-router-thumbnail.png",
    url: "/ai-runner/multi-mcp-agent-router",
    popular: false,
  },
  {
    id: "openai-remote-mcp-tool-bridge",
    name: "OpenAI Remote MCP Tool Bridge",
    description: "Connect OpenAI function calling directly to a remote MCP server.",
    category: "mcp-ai-agents",
    group: "mcp-ai-agents",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/openai-remote-mcp-tool-bridge-thumbnail.png",
    url: "/ai-runner/openai-remote-mcp-tool-bridge",
    popular: false,
  },
  {
    id: "agentic-rag-with-embedding-gemma",
    name: "Agentic RAG with Embedding Gemma",
    description: "Fully local agentic RAG with EmbeddingGemma and Llama 3.2.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/agentic-rag-with-embedding-gemma-thumbnail.png",
    url: "/ai-runner/agentic-rag-with-embedding-gemma",
    popular: false,
  },
  {
    id: "agentic-rag-with-reasoning",
    name: "Agentic RAG with Reasoning",
    description: "Watch the agent's step-by-step reasoning as it retrieves.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/agentic-rag-with-reasoning-thumbnail.png",
    url: "/ai-runner/agentic-rag-with-reasoning",
    popular: false,
  },
  {
    id: "ai-blog-search-rag",
    name: "AI Blog Search (RAG)",
    description: "Agentic search over blog content, built on LangGraph.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-blog-search-rag-thumbnail.png",
    url: "/ai-runner/ai-blog-search-rag",
    popular: false,
  },
  {
    id: "autonomous-rag",
    name: "Autonomous RAG",
    description: "GPT-4o answers from your PDFs, falls back to web search.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/autonomous-rag-thumbnail.png",
    url: "/ai-runner/autonomous-rag",
    popular: false,
  },
  {
    id: "contextual-ai-rag-agent",
    name: "Contextual AI RAG Agent",
    description: "Managed RAG: datastore to grounded chat in minutes.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/contextual-ai-rag-agent-thumbnail.png",
    url: "/ai-runner/contextual-ai-rag-agent",
    popular: false,
  },
  {
    id: "corrective-rag-crag",
    name: "Corrective RAG (CRAG)",
    description: "Retrieval that grades itself and retries before answering.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/corrective-rag-crag-thumbnail.png",
    url: "/ai-runner/corrective-rag-crag",
    popular: false,
  },
  {
    id: "typed-agentic-rag-with-pydantic-ai",
    name: "Typed Agentic RAG with Pydantic AI",
    description: "Validated answers with exact citations, or a refusal when evidence is weak.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/typed-agentic-rag-with-pydantic-ai-thumbnail.png",
    url: "/ai-runner/typed-agentic-rag-with-pydantic-ai",
    popular: false,
  },
  {
    id: "deepseek-local-rag-agent",
    name: "Deepseek Local RAG Agent",
    description: "Local DeepSeek reasoning over your own documents.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/deepseek-local-rag-agent-thumbnail.png",
    url: "/ai-runner/deepseek-local-rag-agent",
    popular: false,
  },
  {
    id: "gemini-agentic-rag",
    name: "Gemini Agentic RAG",
    description: "Query rewriting and web fallback with Gemini Flash Thinking.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/gemini-agentic-rag-thumbnail.png",
    url: "/ai-runner/gemini-agentic-rag",
    popular: false,
  },
  {
    id: "hybrid-search-rag-cloud",
    name: "Hybrid Search RAG (Cloud)",
    description: "Keyword plus vector search feeding Claude.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/hybrid-search-rag-cloud-thumbnail.png",
    url: "/ai-runner/hybrid-search-rag-cloud",
    popular: false,
  },
  {
    id: "llama-3-1-local-rag",
    name: "Llama 3.1 Local RAG",
    description: "Chat with any webpage, fully offline.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/llama-3-1-local-rag-thumbnail.png",
    url: "/ai-runner/llama-3-1-local-rag",
    popular: false,
  },
  {
    id: "local-hybrid-search-rag",
    name: "Local Hybrid Search RAG",
    description: "Hybrid search with everything running on your machine.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/local-hybrid-search-rag-thumbnail.png",
    url: "/ai-runner/local-hybrid-search-rag",
    popular: false,
  },
  {
    id: "multimodal-agentic-rag",
    name: "Multimodal Agentic RAG",
    description: "Text, PDFs, images, audio, and video, answered with citations.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/multimodal-agentic-rag-thumbnail.png",
    url: "/ai-runner/multimodal-agentic-rag",
    popular: false,
  },
  {
    id: "local-rag-agent",
    name: "Local RAG Agent",
    description: "Llama 3.2 and Qdrant, no API keys required.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/local-rag-agent-thumbnail.png",
    url: "/ai-runner/local-rag-agent",
    popular: false,
  },
  {
    id: "rag-as-a-service",
    name: "RAG-as-a-Service",
    description: "A production RAG service in under 50 lines.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/rag-as-a-service-thumbnail.png",
    url: "/ai-runner/rag-as-a-service",
    popular: false,
  },
  {
    id: "rag-agent-with-cohere",
    name: "RAG Agent with Cohere",
    description: "Command R7B retrieval with web-search fallback.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/rag-agent-with-cohere-thumbnail.png",
    url: "/ai-runner/rag-agent-with-cohere",
    popular: false,
  },
  {
    id: "basic-rag-chain",
    name: "Basic RAG Chain",
    description: "The minimal retrieval pipeline, applied to pharma research.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/basic-rag-chain-thumbnail.png",
    url: "/ai-runner/basic-rag-chain",
    popular: false,
  },
  {
    id: "rag-with-database-routing",
    name: "RAG with Database Routing",
    description: "Routes each question to the right database automatically.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/rag-with-database-routing-thumbnail.png",
    url: "/ai-runner/rag-with-database-routing",
    popular: false,
  },
  {
    id: "vision-rag",
    name: "Vision RAG",
    description: "Ask questions about images and PDF pages with Embed-4.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/vision-rag-thumbnail.png",
    url: "/ai-runner/vision-rag",
    popular: false,
  },
  {
    id: "rag-failure-diagnostics-clinic",
    name: "RAG Failure Diagnostics Clinic",
    description: "Find out why your RAG pipeline is wrong, systematically.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/rag-failure-diagnostics-clinic-thumbnail.png",
    url: "/ai-runner/rag-failure-diagnostics-clinic",
    popular: false,
  },
  {
    id: "knowledge-graph-rag-with-citations",
    name: "Knowledge Graph RAG with Citations",
    description: "Multi-hop answers with verifiable source attribution.",
    category: "rag-tutorials",
    group: "rag-tutorials",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/knowledge-graph-rag-with-citations-thumbnail.png",
    url: "/ai-runner/knowledge-graph-rag-with-citations",
    popular: false,
  },
  {
    id: "needle-a-new-way-to-find",
    name: "Needle - A New Way to Find",
    description: "Search webpages by meaning and highlight the strongest source sentence, using a Chrome extension powered by TypeSafe Jev.",
    category: "ai-browser-tools",
    group: "ai-browser-tools",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/needle-a-new-way-to-find-thumbnail.png",
    url: "/ai-runner/needle-a-new-way-to-find",
    popular: false,
  },
  {
    id: "ripple-change-one-thing-find-what-else-needs-to-change",
    name: "Ripple - Change One Thing, Find What Else Needs to Change",
    description: "Find related inconsistencies and suggested fixes as you edit a Google Doc, using TypeSafe Jev and Gemini.",
    category: "ai-browser-tools",
    group: "ai-browser-tools",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ripple-change-one-thing-find-what-else-needs-to-change-thumbnail.png",
    url: "/ai-runner/ripple-change-one-thing-find-what-else-needs-to-change",
    popular: false,
  },
  {
    id: "ai-arxiv-agent-with-memory",
    name: "AI ArXiv Agent with Memory",
    description: "Paper search that remembers your research interests.",
    category: "llm-apps-memory",
    group: "llm-apps-memory",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-arxiv-agent-with-memory-thumbnail.png",
    url: "/ai-runner/ai-arxiv-agent-with-memory",
    popular: false,
  },
  {
    id: "ai-travel-agent-with-memory",
    name: "AI Travel Agent with Memory",
    description: "A travel assistant that remembers your preferences.",
    category: "llm-apps-memory",
    group: "llm-apps-memory",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/ai-travel-agent-with-memory-thumbnail.png",
    url: "/ai-runner/ai-travel-agent-with-memory",
    popular: false,
  },
  {
    id: "llama3-stateful-chat",
    name: "Llama3 Stateful Chat",
    description: "Session-persistent chat with Llama 3.",
    category: "llm-apps-memory",
    group: "llm-apps-memory",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/llama3-stateful-chat-thumbnail.png",
    url: "/ai-runner/llama3-stateful-chat",
    popular: false,
  },
  {
    id: "llm-app-with-personalized-memory",
    name: "LLM App with Personalized Memory",
    description: "A chatbot that keeps context across conversations.",
    category: "llm-apps-memory",
    group: "llm-apps-memory",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/llm-app-with-personalized-memory-thumbnail.png",
    url: "/ai-runner/llm-app-with-personalized-memory",
    popular: false,
  },
  {
    id: "local-chatgpt-clone-with-memory",
    name: "Local ChatGPT Clone with Memory",
    description: "Fully local, with a personal memory per user.",
    category: "llm-apps-memory",
    group: "llm-apps-memory",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/local-chatgpt-clone-with-memory-thumbnail.png",
    url: "/ai-runner/local-chatgpt-clone-with-memory",
    popular: false,
  },
  {
    id: "multi-llm-application-with-shared-memory",
    name: "Multi-LLM Application with Shared Memory",
    description: "Different models, one shared conversation memory.",
    category: "llm-apps-memory",
    group: "llm-apps-memory",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/multi-llm-application-with-shared-memory-thumbnail.png",
    url: "/ai-runner/multi-llm-application-with-shared-memory",
    popular: false,
  },
  {
    id: "chat-with-github-gpt-llama3",
    name: "Chat with GitHub (GPT & Llama3)",
    description: "Any repo, answered in 30 lines of RAG.",
    category: "chat-with-x",
    group: "chat-with-x",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/chat-with-github-gpt-llama3-thumbnail.png",
    url: "/ai-runner/chat-with-github-gpt-llama3",
    popular: false,
  },
  {
    id: "chat-with-gmail",
    name: "Chat with Gmail",
    description: "Ask your inbox questions.",
    category: "chat-with-x",
    group: "chat-with-x",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/chat-with-gmail-thumbnail.png",
    url: "/ai-runner/chat-with-gmail",
    popular: false,
  },
  {
    id: "chat-with-pdf-gpt-llama3",
    name: "Chat with PDF (GPT & Llama3)",
    description: "The classic, in 30 lines of Python.",
    category: "chat-with-x",
    group: "chat-with-x",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/chat-with-pdf-gpt-llama3-thumbnail.png",
    url: "/ai-runner/chat-with-pdf-gpt-llama3",
    popular: false,
  },
  {
    id: "chat-with-research-papers-arxiv-gpt-llama3",
    name: "Chat with Research Papers (ArXiv) (GPT & Llama3)",
    description: "Explore arXiv conversationally with GPT-4o.",
    category: "chat-with-x",
    group: "chat-with-x",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/chat-with-research-papers-arxiv-gpt-llama3-thumbnail.png",
    url: "/ai-runner/chat-with-research-papers-arxiv-gpt-llama3",
    popular: false,
  },
  {
    id: "chat-with-substack",
    name: "Chat with Substack",
    description: "Chat with any newsletter's archive.",
    category: "chat-with-x",
    group: "chat-with-x",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/chat-with-substack-thumbnail.png",
    url: "/ai-runner/chat-with-substack",
    popular: false,
  },
  {
    id: "chat-with-youtube-videos",
    name: "Chat with YouTube Videos",
    description: "Ask videos questions via their transcripts.",
    category: "chat-with-x",
    group: "chat-with-x",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/chat-with-youtube-videos-thumbnail.png",
    url: "/ai-runner/chat-with-youtube-videos",
    popular: false,
  },
  {
    id: "toonify-token-optimization",
    name: "Toonify Token Optimization",
    description: "Reduce LLM API costs by 30-60% using TOON format.",
    category: "llm-optimization-tools",
    group: "llm-optimization-tools",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/toonify-token-optimization-thumbnail.png",
    url: "/ai-runner/toonify-token-optimization",
    popular: false,
  },
  {
    id: "headroom-context-optimization",
    name: "Headroom Context Optimization",
    description: "Reduce LLM API costs by 50-90%.",
    category: "llm-optimization-tools",
    group: "llm-optimization-tools",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/headroom-context-optimization-thumbnail.png",
    url: "/ai-runner/headroom-context-optimization",
    popular: false,
  },
  {
    id: "gemma-3-fine-tuning",
    name: "Gemma 3 Fine-tuning",
    description: "4-bit LoRA with Unsloth, small and readable.",
    category: "llm-finetuning",
    group: "llm-finetuning",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/gemma-3-fine-tuning-thumbnail.png",
    url: "/ai-runner/gemma-3-fine-tuning",
    popular: false,
  },
  {
    id: "llama-3-2-fine-tuning",
    name: "Llama 3.2 Fine-tuning",
    description: "Fine-tune in 30 lines, free on Colab.",
    category: "llm-finetuning",
    group: "llm-finetuning",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/llama-3-2-fine-tuning-thumbnail.png",
    url: "/ai-runner/llama-3-2-fine-tuning",
    popular: false,
  },
  {
    id: "google-adk-crash-course",
    name: "Google ADK Crash Course",
    description: "Starter agent, structured outputs, tools (built-in, function, third-party, MCP), memory, callbacks, plugins, and multi-agent patterns. Model-agnostic.",
    category: "ai-agent-framework-crash-course",
    group: "ai-agent-framework-crash-course",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/google-adk-crash-course-thumbnail.png",
    url: "/ai-runner/google-adk-crash-course",
    popular: false,
  },
  {
    id: "openai-agents-sdk-crash-course",
    name: "OpenAI Agents SDK Crash Course",
    description: "Starter agent, function calling, structured outputs, tools, memory, evaluation, handoffs, swarm orchestration, and routing logic.",
    category: "ai-agent-framework-crash-course",
    group: "ai-agent-framework-crash-course",
    icon: React.createElement(Wand2),
    image: "https://bzxohkrxcwodllketcpz.supabase.co/storage/v1/object/public/app-assets/thumbnails/openai-agents-sdk-crash-course-thumbnail.png",
    url: "/ai-runner/openai-agents-sdk-crash-course",
    popular: false,
  },
];

// Export apps data with AI-generated thumbnails where available
export const appsData: App[] = updateAppThumbnails(rawAppsData);

export { rawAppsData };
